import type { PropsWithChildren } from 'react'
import React, { useEffect, useState } from 'react'
import { useId } from '../hooks/useId'
import Typography from '../Typography/Typography'
import type { AriaLiveRole } from '../utilities/aria'
import Circular from './Circular'
import Linear from './Linear'
import { ProgressContext, useProgress } from './ProgressContext'

type DescriptionProps = {
  inverse?: boolean
  role?: AriaLiveRole
}

function Description({
  children,
  inverse = false,
  role,
}: PropsWithChildren<DescriptionProps>): JSX.Element {
  const [, setDescriptionId] = useProgress()
  const id = `coconut-progress-message-${useId()}`

  useEffect(() => {
    setDescriptionId(id)
  }, [id, setDescriptionId])

  return (
    <div className="mt-4" id={id} role={role}>
      <Typography color={inverse ? 'inverse' : 'initial'} variant="body2">
        {children}
      </Typography>
    </div>
  )
}

type BaseProgressProps = {
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-valuetext'?: string
  color?: ProgressColor
  id?: string
  size?: ProgressSize
  variant?: ProgressVariant
}

type DeterminateProgressProps = BaseProgressProps & {
  value: number
}

type IndeterminateProgressProps = BaseProgressProps & {
  value?: never
}

type ProgressColor = 'info' | 'inverse' | 'primary' | 'secondary'

type ProgressColors = {
  [key in ProgressColor]: {
    bar: {
      circular: string
      linear: string
    }
    track: {
      linear: string
    }
  }
}

type ProgressProps = DeterminateProgressProps | IndeterminateProgressProps

type ProgressVariant = 'circular' | 'linear'

export type ProgressSize = 'large' | 'medium' | 'small'

export type ProgressVariantProps = {
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-valuetext'?: string
  'aria-valuemax'?: number
  'aria-valuemin'?: number
  'aria-valuenow'?: number
  color: ProgressColor
  id?: string
  size?: ProgressSize
  value?: number
}

export const colors: ProgressColors = {
  info: {
    bar: {
      circular: `text-info-main`,
      linear: `bg-info-main`,
    },
    track: {
      linear: `bg-info-20`,
    },
  },
  inverse: {
    bar: {
      circular: `text-white-100`,
      linear: `bg-white-100`,
    },
    track: {
      linear: `bg-white-20`,
    },
  },
  primary: {
    bar: {
      circular: `text-primary-main`,
      linear: `bg-primary-main`,
    },
    track: {
      linear: `bg-primary-20`,
    },
  },
  secondary: {
    bar: {
      circular: `text-secondary-main`,
      linear: `bg-secondary-main`,
    },
    track: {
      linear: `bg-secondary-20`,
    },
  },
}

function Progress({
  'aria-label': label,
  'aria-labelledby': labelledBy,
  'aria-valuetext': valueText,
  children,
  color = 'primary',
  id,
  size = 'large',
  value,
  variant = 'linear',
}: PropsWithChildren<ProgressProps>): JSX.Element {
  const [descriptionId, setDescriptionId] = useState<string>()

  const props = {
    'aria-valuemax': value === undefined ? undefined : 100,
    'aria-valuemin': value === undefined ? undefined : 0,
    'aria-valuenow': value === undefined ? undefined : Math.round(value),
  }

  if (variant === 'circular') {
    return (
      <ProgressContext.Provider value={[descriptionId, setDescriptionId]}>
        <Circular
          aria-label={label}
          aria-labelledby={labelledBy}
          aria-valuetext={valueText}
          color={color}
          id={id}
          size={size}
          value={value}
          {...props}
        >
          {children}
        </Circular>
      </ProgressContext.Provider>
    )
  }

  return (
    <Linear
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-valuetext={valueText}
      color={color}
      id={id}
      value={value}
      {...props}
    />
  )
}

Progress.Description = Description

export default Progress
